import styled from "@emotion/styled/macro";
import {
  Button,
  LoadingIndicator,
  MOBILE_BREAKPOINT,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import { useEffect, useState, type KeyboardEvent, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { ListOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import BackgroundImage from "../assets/images/offer-selector-bg.png";
import SunrunLogo from "../assets/images/sunrun-logo.svg";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { ContactSalesRepModal } from "components/molecules/ContactSalesRepModal";
import {
  BACKUP_ID,
  NON_EXPORT_BACKUP_PRODUCT_ID,
  STANDALONE_EV_CHARGER_PRODUCT_ID,
  STANDALONE_PRODUCT_ID,
  STORAGE_ADDON_PRODUCT_ID,
} from "constants/bundleAndProductIds";
import { financialProductDisplayText } from "constants/financialProducts";
import { useAuthToken } from "hooks/useAuthToken";
import { useCustomerSalesRep } from "hooks/useCustomerSalesRep";
import { customerListOffers } from "services/storeFront";
import { useUsageData } from "utils/swrHooks";
import { useNavigateToReview } from "utils/useNavigateHelper";
import {
  CUSTOMER_SELF_CHECKOUT__CLICKED_EMAIL_SALES_REP_CTA_OFFER_SELECTOR_PAGE,
  CUSTOMER_SELF_CHECKOUT__CLICKED_PHONE_SALES_REP_CTA_OFFER_SELECTOR_PAGE,
  CUSTOMER_SELF_CHECKOUT__CLICKED_SEND_MESSAGE_SALES_REP_CTA_OFFER_SELECTOR_PAGE,
} from "constants/mixpanelEvents";
import { formatCurrency, formatPercent } from "utils/formatHelpers";
import {
  NEW_HOMES_CONTACT_BUTTON_LABEL,
  NEW_HOMES_SELECTOR_BOTTOM_TEXT,
} from "constants/newHomes";
import { isNewHomesSalesDivision } from "utils/newHomes";
import { excludeBuilderOffers } from "utils/builderOffer";

const ALTERNATIVE_TABLET_BREAKPOINT = 1199;

function getFilteredProductCount(
  offer: ListOffer,
  productId: string
): number | undefined {
  return offer.products.filter((item) => item?.productId === productId)[0]
    ?.count;
}

function getOfferListMetrics(offer: ListOffer) {
  return {
    storageAddOnBatteryCount: getFilteredProductCount(
      offer,
      STORAGE_ADDON_PRODUCT_ID
    ),
    standaloneBatteryCount: getFilteredProductCount(
      offer,
      STANDALONE_PRODUCT_ID
    ),
    standaloneEvChargerCount: getFilteredProductCount(
      offer,
      STANDALONE_EV_CHARGER_PRODUCT_ID
    ),
    backupCount: getFilteredProductCount(offer, BACKUP_ID),
    nonExportBackupCount: getFilteredProductCount(
      offer,
      NON_EXPORT_BACKUP_PRODUCT_ID
    ),
  };
}

function getOfferTitle(
  offer: ListOffer,
  metrics: ReturnType<typeof getOfferListMetrics>
): string {
  const {
    storageAddOnBatteryCount,
    standaloneBatteryCount,
    standaloneEvChargerCount,
    backupCount,
    nonExportBackupCount,
  } = metrics;
  const items: string[] = !!offer?.solarCount ? ["solar"] : [];
  if (
    offer.isOnlyEvCharger &&
    standaloneEvChargerCount &&
    standaloneEvChargerCount > 0
  ) {
    items.push("Standalone EV charger");
  } else if (
    (storageAddOnBatteryCount && storageAddOnBatteryCount > 0) ||
    (backupCount && backupCount > 0) ||
    (nonExportBackupCount && nonExportBackupCount > 0) ||
    (standaloneBatteryCount && standaloneBatteryCount > 0)
  ) {
    items.push("battery backup");
  }

  const title = items.join(" + ");

  return title ? title[0].toUpperCase() + title.slice(1) : "Sunrun offer";
}

function OfferCardItemsList({ offer }: { offer: ListOffer }) {
  const metrics = getOfferListMetrics(offer);
  const {
    storageAddOnBatteryCount,
    standaloneBatteryCount,
    standaloneEvChargerCount,
  } = metrics;

  return (
    <ItemsList>
      {offer.solarCount ? (
        <Item>
          {offer.solarCount} Solar Panel{offer.solarCount === 1 ? "" : "s"}
        </Item>
      ) : null}
      {storageAddOnBatteryCount ? (
        <Item>
          {storageAddOnBatteryCount}
          {storageAddOnBatteryCount === 1 ? " Battery" : " Batteries"}
        </Item>
      ) : null}
      {standaloneBatteryCount ? (
        <Item>
          {standaloneBatteryCount}
          {standaloneBatteryCount === 1 ? " Battery" : " Batteries"}
        </Item>
      ) : null}
      {standaloneEvChargerCount ? (
        <Item>
          {standaloneEvChargerCount}
          {standaloneEvChargerCount === 1 ? " EV charger" : " EV chargers"}
        </Item>
      ) : null}
      {offer.products.map((product, index) => {
        return (
          <Item key={product.name ?? index}>
            {product.count} {product.name}
          </Item>
        );
      })}
    </ItemsList>
  );
}

const LegacyOfferCard = ({ offer }: { offer: ListOffer }) => {
  const navigateToReview = useNavigateToReview();
  const metrics = getOfferListMetrics(offer);
  const offerTitle = getOfferTitle(offer, metrics);

  let financing: string | undefined;
  switch (offer.financing) {
    case "Cash":
      financing = "Purchase";
      break;
    case "Loan":
      financing = "Loan";
      break;
    case "Monthly":
      financing = "Monthly";
      break;
    case "Prepaid":
      financing = "Pre-Paid";
      break;
    case "FlexMonthly":
      financing = financialProductDisplayText.FlexMonthly;
      break;
    default:
      financing = undefined;
  }

  let financingPrefix: string | undefined;
  switch (offer.financing) {
    case "Cash":
    case "Prepaid":
      financingPrefix = "Pay upfront:";
      break;
    case "Monthly":
    case "FlexMonthly":
    case "Loan":
      financingPrefix = "Pay over time:";
      break;
    default:
      financingPrefix = undefined;
  }

  return (
    <LegacyOfferCardContainer onClick={() => navigateToReview(offer.id)}>
      <LegacyCardContent>
        <CardHeader>
          <Typography
            size={24}
            lineHeight={24}
            fontWeight="medium"
            color={tokens.BRAND_HERO_BLUE}
          >
            {offerTitle}
          </Typography>
          <Pill>
            {financingPrefix} {financing}
          </Pill>
        </CardHeader>
        <OfferCardItemsList offer={offer} />
      </LegacyCardContent>
      <LegacyNavigateButton type="button" aria-label="View offer details">
        <SvgIcon
          width="20px"
          name={SvgNames.ChevronRight}
          color={tokens.BRAND_HERO_BLUE}
        />
      </LegacyNavigateButton>
    </LegacyOfferCardContainer>
  );
};

const NewHomesOfferCard = ({
  offer,
  onContact,
}: {
  offer: ListOffer;
  onContact: () => void;
}) => {
  const navigateToReview = useNavigateToReview();
  const purchase = offer.financing === "Cash";
  const metrics = getOfferListMetrics(offer);
  const offerTitle = getOfferTitle(offer, metrics);

  let financingPrefix: ReactNode;
  switch (offer.financing) {
    case "Cash":
    case "Prepaid":
      financingPrefix = <Typography>Purchase</Typography>;
      break;
    case "Monthly":
    case "FlexMonthly":
    case "Loan":
      financingPrefix = (
        <>
          <Typography>
            {formatCurrency(offer.payment)} monthly payment
          </Typography>
          <Typography>
            {formatPercent(offer.annualEscalator ?? 0)} annual escalator
          </Typography>
        </>
      );
      break;
    default:
      financingPrefix = null;
  }

  const handleCardActivate = () => {
    if (purchase) {
      onContact();
    } else {
      navigateToReview(offer.id);
    }
  };

  const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardActivate();
    }
  };

  return (
    <NewHomesOfferCardContainer
      role="button"
      tabIndex={0}
      onClick={handleCardActivate}
      onKeyDown={handleCardKeyDown}
    >
      <NewHomesCardContent>
        <CardHeader>
          <Typography
            size={24}
            lineHeight={24}
            fontWeight="medium"
            color={tokens.BRAND_HERO_BLUE}
          >
            {offerTitle}
          </Typography>
        </CardHeader>
        <OfferCardItemsList offer={offer} />
      </NewHomesCardContent>
      <NewHomesCardCornerIcon aria-hidden>
        <SvgIcon
          width="20px"
          name={purchase ? SvgNames.InfoOutline : SvgNames.ChevronRight}
          color={tokens.BRAND_HERO_BLUE}
        />
      </NewHomesCardCornerIcon>
      <NewHomesCardFooter>{financingPrefix}</NewHomesCardFooter>
    </NewHomesOfferCardContainer>
  );
};

const OfferSelectorPage = () => {
  const { prospectId, offerId: offerIdParam } = useParams();

  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const { prospect, isLoading: isUsageLoading } = useUsageData(prospectId);
  const { data: salesRep, error: salesRepError } = useCustomerSalesRep();

  const [offers, setOffers] = useState<ListOffer[]>();
  const [error, setError] = useState<Error>();
  const [isOffersLoading, setIsOffersLoading] = useState<boolean>(true);
  const [showContactSalesRepModal, setShowContactSalesRepModal] =
    useState<boolean>(false);

  const isNewHomes = isNewHomesSalesDivision(prospect?.salesDivision);

  const openContactSalesRepModal = () => {
    setShowContactSalesRepModal(true);
  };

  const closeContactSalesRepModal = () => {
    setShowContactSalesRepModal(false);
  };

  const isMobile = useSingletonMobileMediaQuery();

  useEffect(() => {
    if (!proposalToken || !prospectId) return;
    setIsOffersLoading(true);

    customerListOffers(proposalToken)
      ?.then((data) => {
        if (data && data.length > 0) {
          setOffers(excludeBuilderOffers(data));
        }
      })
      .catch((error) => {
        setIsOffersLoading(false);
        setError(
          new Error("There was a problem fetching the offer list data.", {
            cause: error,
          })
        );
      })
      .finally(() => {
        setIsOffersLoading(false);
      });
  }, [proposalToken, prospectId]);

  if (isOffersLoading || !offers || !prospect) {
    return <LoadingIndicator fullScreen color="black" />;
  }

  if (error) {
    return (
      <ErrorComponent
        context="OfferSelectorPage"
        action="Get Offer List"
        error={error}
      />
    );
  }

  const offersList =
    offers && offers.length > 0 ? (
      offers.map((offer) =>
        isNewHomes ? (
          <NewHomesOfferCard
            key={offer.id}
            offer={offer}
            onContact={openContactSalesRepModal}
          />
        ) : (
          <LegacyOfferCard key={offer.id} offer={offer} />
        )
      )
    ) : (
      <Typography>No available offers found</Typography>
    );

  return (
    <Page>
      <Header>
        <MaxWidth>
          <Wrapper>
            <StyledButton color="link" onClick={openContactSalesRepModal}>
              {isNewHomes
                ? NEW_HOMES_CONTACT_BUTTON_LABEL
                : "Contact Sales Rep"}
            </StyledButton>
            <MobileView>
              <LogoImage src={SunrunLogo} alt="Sunrun Logo" />
            </MobileView>
          </Wrapper>
          <InfoWrapper>
            <Name>
              {prospect?.customerFirstName} {prospect?.customerLastName}
            </Name>
            {!isUsageLoading && (
              <Address>
                {prospect?.customerStreet}, {prospect?.customerCity},{" "}
                {prospect?.customerState} {prospect?.customerZipCode}
              </Address>
            )}
          </InfoWrapper>
          <DesktopView>
            <LogoImage src={SunrunLogo} alt="Sunrun Logo" />
          </DesktopView>
        </MaxWidth>
        {showContactSalesRepModal && salesRep && !salesRepError && (
          <ContactSalesRepModal
            testId="offer-selector-page-contact-sales-rep-modal"
            open={showContactSalesRepModal}
            onClose={closeContactSalesRepModal}
            prospectId={prospectId ?? ""}
            offerId={offerIdParam}
            isCustomer={true}
            isNewHomes={isNewHomes}
            salesRep={salesRep}
            mixpanelEmailEvent={
              CUSTOMER_SELF_CHECKOUT__CLICKED_EMAIL_SALES_REP_CTA_OFFER_SELECTOR_PAGE
            }
            mixpanelPhoneEvent={
              CUSTOMER_SELF_CHECKOUT__CLICKED_PHONE_SALES_REP_CTA_OFFER_SELECTOR_PAGE
            }
            mixpanelMessageEvent={
              CUSTOMER_SELF_CHECKOUT__CLICKED_SEND_MESSAGE_SALES_REP_CTA_OFFER_SELECTOR_PAGE
            }
          />
        )}
      </Header>
      {isNewHomes ? (
        <ContentNewHomes>
          <OfferListTitle
            color={tokens.BRAND_HERO_BLUE}
            size={isMobile ? 20 : 28}
          >
            Select an option below to view the details of your offers
          </OfferListTitle>
          <OffersContainerNewHomes>{offersList}</OffersContainerNewHomes>
          <ContactContainer>
            <Typography size={18}>{NEW_HOMES_SELECTOR_BOTTOM_TEXT}</Typography>
            <ContactSalesRepButton size="sm" onClick={openContactSalesRepModal}>
              {NEW_HOMES_CONTACT_BUTTON_LABEL}
            </ContactSalesRepButton>
          </ContactContainer>
          <BackgroundOverlayNewHomes />
        </ContentNewHomes>
      ) : (
        <ContentLegacy>
          <OfferListTitle
            color={tokens.BRAND_HERO_BLUE}
            size={isMobile ? 20 : 28}
          >
            Select an option below to view the details of your offers
          </OfferListTitle>
          <OffersContainerLegacy>{offersList}</OffersContainerLegacy>
          <BackgroundOverlayLegacy />
        </ContentLegacy>
      )}
    </Page>
  );
};

const Page = styled.div`
  height: 100vh;
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const ContentLegacy = styled.div`
  padding: 48px;

  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`;

const ContentNewHomes = styled.div`
  padding: 48px;
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`;

const BackgroundOverlayLegacy = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.3;
  background: linear-gradient(180deg, #fffbf0 0%, #a5c9ff 100%);
  z-index: -1;
`;

const BackgroundOverlayNewHomes = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.3;
  background: linear-gradient(0deg, #fffbf0 0%, #a5c9ff 100%);
  z-index: -1;
`;

const MaxWidth = styled.div`
  max-width: 1040px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 480px) {
    gap: 28px;
    flex-direction: column;
  }
`;

const Wrapper = styled.div`
  @media (max-width: 480px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
`;

const DesktopView = styled.div`
  display: block;
  @media (max-width: 480px) {
    display: none;
  }
`;

const MobileView = styled.div`
  display: none;
  @media (max-width: 480px) {
    display: block;
  }
`;
const Header = styled.div`
  display: flex;
  background-image: url(${BackgroundImage});
  background-size: cover;
  background-position: center;
  padding: 16px;
  border-bottom: 1px solid ${tokens.OFFWHITE_40};
  padding: 60px 60px 40px;

  @media (max-width: 768px) {
    padding: 40px;
  }
  @media (max-width: 480px) {
    padding: 24px 16px 32px;
  }
`;

const LogoImage = styled.img`
  width: 130px;

  @media (max-width: 768px) {
    width: 84px;
  }
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const Name = styled.p`
  margin: 0;
  color: #fff;
  text-align: center;
  font-family: Roobert;
  font-size: 40px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 56px */
  letter-spacing: -0.8px;
`;

const Address = styled.p`
  margin: 0;
  color: #fff;
  text-align: center;
  font-family: Roobert;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 22.4px */
`;

const OfferListTitle = styled(Typography)`
  line-height: 110%;
  letter-spacing: -0.84px;
`;

const OffersContainerLegacy = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 22px;
  padding: 22px 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const OffersContainerNewHomes = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 22px;
  padding: 22px 0;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Pill = styled.div`
  background: ${tokens.INFO_80};
  color: ${tokens.BRAND_HERO_BLUE};
  padding: 3px 8px;
  border-radius: 30px;
  font-family: Roobert;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%; /* 14.4px */
  letter-spacing: 0.24px;
`;

const LegacyCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  flex-grow: 1;
`;

const NewHomesCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  flex-grow: 1;
`;

const LegacyOfferCardContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${tokens.OFFWHITE_40};
  background: ${tokens.WHITE};
  cursor: pointer;
  &:hover {
    button {
      background: ${tokens.OFFWHITE_50};
    }
  }
`;

const NewHomesOfferCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${tokens.OFFWHITE_40};
  background: ${tokens.WHITE};
  position: relative;
  cursor: pointer;
  transition: border 0.15s ease-in-out;

  &:hover {
    border: 1px solid ${tokens.BRAND_HERO_BLUE};
  }

  &:focus-visible {
    outline: 2px solid ${tokens.BRAND_HERO_BLUE};
    outline-offset: 2px;
  }
`;

const CardHeader = styled.div`
  color: ${tokens.BRAND_HERO_BLUE};
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 12px;
  color: ${tokens.WHITE};
`;

const ItemsList = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(2, 1fr);
  color: ${tokens.TINTS_OFF_WHITE_20};
  grid-gap: 8px;
  flex-grow: 1;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #6f6d68;
  padding-left: 8px;

  /* web-type-sizes/14_regular */
  font-family: Roobert;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 19.6px */
  letter-spacing: -0.28px;

  &::before {
    content: "•";
    position: absolute;
    margin-left: -8px;
  }
`;

const LegacyNavigateButton = styled.button`
  border: none;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  border-left: 1px solid ${tokens.OFFWHITE_40};
  background: ${tokens.OFFWHITE_70};
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: ${tokens.OFFWHITE_50};
  }
`;

const NewHomesCardCornerIcon = styled.div`
  position: absolute;
  right: 8px;
  top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  background: ${tokens.WHITE};
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const StyledButton = styled(Button)`
  color: ${tokens.WHITE};
  border-color: ${tokens.WHITE};
`;

const NewHomesCardFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  background-color: ${tokens.INFO_80};
  border-radius: 0 0 8px 8px;
  padding: 12px 16px;
`;

const ContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 12px;
  margin-top: auto;

  @media (max-width: ${ALTERNATIVE_TABLET_BREAKPOINT}px) and (orientation: landscape) {
    flex-direction: row;
    gap: 40px;
    align-items: center;
  }
`;

const ContactSalesRepButton = styled(Button)`
  color: ${tokens.BRAND_HERO_BLUE};
  border: 1px solid ${tokens.BRAND_HERO_BLUE};
  background-color: transparent;
  font-size: 16px;
  font-weight: 500;
  width: fit-content;
  flex-shrink: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

export { OfferSelectorPage };
